import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Chip,
  Button,
  Paper,
  Grid,
  IconButton,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import WorkIcon from "@mui/icons-material/Work";
import BusinessIcon from "@mui/icons-material/Business";
import Cookies from "js-cookie";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3500";

const TIME_SLOTS = [
  "09:00",
  "10:00",
  "11:00",
  "12:00",
  "13:00",
  "14:00",
  "15:00",
  "16:00",
];

function formatTime(t) {
  const [h, m] = t.split(":");
  const date = new Date();
  date.setHours(h, m);
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export default function ScheduleJobInterview() {
  const [roles, setRoles] = useState([]);
  const [appliedJobs, setAppliedJobs] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState("");
  const [schedules, setSchedules] = useState([]);
  const [editId, setEditId] = useState(null);
  const [activeTab, setActiveTab] = useState("roles"); // 'roles' or 'jobs'
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);

  const token = Cookies.get("token");

  useEffect(() => {
    fetch(`${API_BASE}/api/profile/user-job-roles`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => setRoles(data));

    fetch(`${API_BASE}/api/profile/interview-schedule`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => setSchedules(data));

    // Fetch applied jobs
    fetch(`${API_BASE}/api/job-postings/applied`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => setAppliedJobs(Array.isArray(data) ? data : []));
  }, []);

  const today = new Date();
  const maxDate = new Date();
  maxDate.setMonth(today.getMonth() + 6);

  const handleSave = async () => {
    if (!selectedRole || !selectedDate || !selectedTime) return;
    // Fix timezone issue by formatting date manually
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
    const day = String(selectedDate.getDate()).padStart(2, "0");
    const payload = {
      role_id:
        selectedRole.job_role_id || selectedRole.role_id || selectedRole.id,
      date: `${year}-${month}-${day}`,
      time: selectedTime + ":00",
    };

    // Add job_id if a specific job is selected
    if (selectedJob) {
      payload.job_id = selectedJob.id;
    }

    if (editId) {
      await fetch(`${API_BASE}/api/profile/interview-schedule/${editId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
    } else {
      await fetch(`${API_BASE}/api/profile/interview-schedule`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
    }
    setEditId(null);
    setSelectedRole(null);
    setSelectedJob(null);
    setSelectedDate(null);
    setSelectedTime("");
    fetch(`${API_BASE}/api/profile/interview-schedule`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => setSchedules(data));
  };

  const handleEdit = (sch) => {
    setEditId(sch.id);
    setSelectedRole(
      roles.find((r) => (r.job_role_id || r.role_id || r.id) === sch.role_id),
    );
    // Fix timezone issue by creating date in local timezone
    const [year, month, day] = sch.date.split("-").map(Number);
    setSelectedDate(new Date(year, month - 1, day));
    setSelectedTime(sch.time.slice(0, 5));
  };

  const handleDelete = async (id) => {
    await fetch(`${API_BASE}/api/profile/interview-schedule/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    setSchedules(schedules.filter((s) => s.id !== id));
  };

  return (
    <Box sx={{ maxWidth: 700, mx: "auto", mt: 4 }}>
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
        Schedule Job Interview
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3 }}>
        Schedule an interview to demonstrate your expertise and get approved for
        job opportunities
      </Typography>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Roles you are requesting interview for
      </Typography>
      <Box sx={{ mb: 3 }}>
        {roles.map((role) => (
          <Paper
            key={role.id || role.job_role_id || role.role_id}
            elevation={
              selectedRole &&
              (selectedRole.id ||
                selectedRole.job_role_id ||
                selectedRole.role_id) ===
                (role.id || role.job_role_id || role.role_id)
                ? 4
                : 1
            }
            sx={{
              display: "inline-block",
              mr: 2,
              mb: 2,
              px: 3,
              py: 2,
              borderRadius: 2,
              border:
                selectedRole &&
                (selectedRole.id ||
                  selectedRole.job_role_id ||
                  selectedRole.role_id) ===
                  (role.id || role.job_role_id || role.role_id)
                  ? "2px solid #6C5CE7"
                  : "1px solid #e0e0e0",
              cursor: "pointer",
              minWidth: 220,
              background:
                selectedRole &&
                (selectedRole.id ||
                  selectedRole.job_role_id ||
                  selectedRole.role_id) ===
                  (role.id || role.job_role_id || role.role_id)
                  ? "rgba(108,92,231,0.07)"
                  : "#fff",
              transition: "box-shadow 0.2s, border 0.2s",
            }}
            onClick={() => setSelectedRole(role)}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              {role.job_role_name || role.role_name || role.name}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
              {role.job_function_name && (
                <Chip
                  label={role.job_function_name}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              )}
              {role.employment_type && (
                <Chip
                  label={role.employment_type}
                  size="small"
                  variant="outlined"
                />
              )}
              {role.location_type && (
                <Chip
                  label={role.location_type}
                  size="small"
                  variant="outlined"
                />
              )}
              {role.will_relocate &&
                (role.will_relocate === true ||
                  role.will_relocate === "Yes") && (
                  <Chip
                    label="Willing to relocate"
                    size="small"
                    variant="outlined"
                  />
                )}
            </Box>
          </Paper>
        ))}
      </Box>
      <Typography variant="subtitle2" sx={{ mb: 2 }}>
        Jobs you have applied for
      </Typography>
      <TableContainer component={Paper} sx={{ mb: 4, overflowX: "auto" }}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "20%" }}>Job Title</TableCell>
              <TableCell sx={{ width: "20%" }}>Company</TableCell>
              <TableCell sx={{ width: "15%" }}>Applied Date</TableCell>
              <TableCell sx={{ width: "15%" }}>Status</TableCell>
              <TableCell sx={{ width: "30%" }}>Schedule</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(Array.isArray(appliedJobs) ? appliedJobs : []).map((job) => {
              // Find if a schedule exists for this job
              const jobSchedule = schedules.find(
                (sch) => sch.job_id === job.id,
              );
              return (
                <TableRow key={job.id}>
                  <TableCell>{job.job_role_name}</TableCell>
                  <TableCell>{job.company_name}</TableCell>
                  <TableCell>
                    {new Date(job.applied_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>{job.application_status}</TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        minWidth: 200,
                      }}
                    >
                      {jobSchedule && (
                        <Chip
                          label={`Scheduled: ${new Date(
                            jobSchedule.date,
                          ).toLocaleDateString()} ${jobSchedule.time.slice(
                            0,
                            5,
                          )}`}
                          color="success"
                          size="small"
                          sx={{ alignSelf: "flex-start" }}
                        />
                      )}
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => {
                          setSelectedJob(job);
                          // If rescheduling, prefill date/time BEFORE opening dialog
                          if (jobSchedule) {
                            // Fix timezone issue by creating date in local timezone
                            let newDate;
                            if (jobSchedule.date.includes("T")) {
                              // Handle ISO date string (e.g., '2025-07-16T18:30:00.000Z')
                              newDate = new Date(jobSchedule.date);
                            } else {
                              // Handle simple date string (e.g., '2025-07-16')
                              const [year, month, day] = jobSchedule.date
                                .split("-")
                                .map(Number);
                              newDate = new Date(year, month - 1, day);
                            }
                            if (!isNaN(newDate)) {
                              setSelectedDate(newDate);
                            } else {
                              setSelectedDate(null);
                            }
                            setSelectedTime(jobSchedule.time.slice(0, 5));
                          } else {
                            setSelectedDate(null);
                            setSelectedTime("");
                          }
                          // Open dialog after setting the date
                          setScheduleDialogOpen(true);
                        }}
                        sx={{ minWidth: 120, alignSelf: "flex-start" }}
                      >
                        {jobSchedule ? "Reschedule" : "Schedule"}
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog
        open={scheduleDialogOpen}
        onClose={() => setScheduleDialogOpen(false)}
      >
        <DialogTitle
          sx={{
            backgroundColor: "#6c5ce7",
            color: "white",
          }}
        >
          Schedule Interview for {selectedJob?.job_role_name}
        </DialogTitle>
        <DialogContent
          sx={{
            overflowY: "auto",
            maxHeight: "80vh",
            "&::-webkit-scrollbar": { width: 8 },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#ccc",
              borderRadius: 8,
            },
          }}
        >
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
            Pick a date
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              value={selectedDate}
              minDate={today}
              maxDate={maxDate}
              onChange={setSelectedDate}
              slotProps={{
                textField: {
                  fullWidth: true,
                  placeholder: "MM/DD/YYYY",
                  sx: { mb: 2 },
                },
              }}
            />
          </LocalizationProvider>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 2 }}>
            {TIME_SLOTS.map((slot) => (
              <Button
                key={slot}
                variant={selectedTime === slot ? "contained" : "outlined"}
                onClick={() => setSelectedTime(slot)}
                sx={{ minWidth: 110 }}
              >
                {formatTime(slot)}
              </Button>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={async () => {
              if (!selectedJob || !selectedDate || !selectedTime) return;
              const jobSchedule = schedules.find(
                (sch) => sch.job_id === selectedJob.id,
              );
              // Fix timezone issue by formatting date manually
              const year = selectedDate.getFullYear();
              const month = String(selectedDate.getMonth() + 1).padStart(
                2,
                "0",
              );
              const day = String(selectedDate.getDate()).padStart(2, "0");
              const payload = {
                role_id: selectedJob.job_role_id,
                job_id: selectedJob.id,
                date: `${year}-${month}-${day}`,
                time: selectedTime + ":00",
              };
              if (jobSchedule) {
                // Update existing schedule
                await fetch(
                  `${API_BASE}/api/profile/interview-schedule/${jobSchedule.id}`,
                  {
                    method: "PUT",
                    headers: {
                      "Content-Type": "application/json",
                      Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify(payload),
                  },
                );
              } else {
                // Create new schedule
                await fetch(`${API_BASE}/api/profile/interview-schedule`, {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                  },
                  body: JSON.stringify(payload),
                });
              }
              setScheduleDialogOpen(false);
              setSelectedJob(null);
              setSelectedDate(null);
              setSelectedTime("");
              fetch(`${API_BASE}/api/profile/interview-schedule`, {
                headers: { Authorization: `Bearer ${token}` },
              })
                .then((res) => res.json())
                .then((data) => setSchedules(data));
            }}
            disabled={!selectedDate || !selectedTime}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
